/**
 * @file consentBridge.ts
 * @brief One place that asks "How VERBLIZR uses AI" and answers with a promise.
 *
 * @details
 * - `ensureAiConsent()`: call before starting a recording, interpretation,
 *   Ask or import. Resolves true at once when the server doesn't need an
 *   answer (or can't say: the route isn't deployed yet), else shows the sheet
 *   and resolves with the user's choice. "Not now" = false, nothing is sent.
 * - `askForAiConsent(wording)`: show the sheet now (403 AI_CONSENT_REQUIRED,
 *   or Data & sharing). Two callers at once share one sheet and one answer.
 * - The API client calls the registered handler on a 403
 *   AI_CONSENT_REQUIRED and retries the same call on "Agree" (B7 rule 3: a
 *   finished recording is never dropped).
 * `AiConsentHost` (mounted once in App) renders the sheet from this store.
 *
 * @author DES (prepared with Claude)
 * @date 22-10-2026
 */

import { apiGet, setAiConsentHandler } from "@/shared/api/client";
import { authEndpoints } from "@/shared/api/endpoints";
import { queryClient } from "@/shared/api/queryClient";
import {
  parseAiConsentState,
  shouldAskFirst,
  wordingFromRefusal,
  type AiConsentState,
  type AiConsentWording,
} from "./aiConsent";

export const aiConsentKey = ["auth", "ai-consent"] as const;

export type AiConsentRequest = { wording: AiConsentWording | null };

let pending: { request: AiConsentRequest; promise: Promise<boolean>; resolve: (ok: boolean) => void } | null =
  null;
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((l) => l());

export function subscribeAiConsent(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** The open question, or null. Stable between changes (useSyncExternalStore). */
export function getAiConsentRequest(): AiConsentRequest | null {
  return pending?.request ?? null;
}

export function askForAiConsent(wording?: AiConsentWording | null): Promise<boolean> {
  if (pending) {
    if (wording && !pending.request.wording) {
      pending.request = { wording };
      emit();
    }
    return pending.promise;
  }
  let resolve!: (ok: boolean) => void;
  const promise = new Promise<boolean>((r) => (resolve = r));
  pending = { request: { wording: wording ?? null }, promise, resolve };
  emit();
  return promise;
}

/** The sheet's answer. "Agree" has already been saved by the host. */
export function settleAiConsent(ok: boolean) {
  const p = pending;
  pending = null;
  emit();
  p?.resolve(ok);
}

/** The sheet swaps in newer wording (409 AI_CONSENT_VERSION_STALE). */
export function replaceAiConsentWording(wording: AiConsentWording) {
  if (!pending) return;
  pending.request = { wording };
  emit();
}

export async function fetchAiConsentState(): Promise<AiConsentState | null> {
  return parseAiConsentState(await apiGet<unknown>(authEndpoints.aiConsent));
}

/** Before anything that sends audio or text to an AI provider. */
export async function ensureAiConsent(): Promise<boolean> {
  let state: AiConsentState | null = null;
  try {
    state = await queryClient.fetchQuery({
      queryKey: aiConsentKey,
      queryFn: fetchAiConsentState,
      staleTime: 5 * 60 * 1000,
    });
  } catch {
    // Not deployed yet (404) or offline: don't ask; the server's 403 decides.
    return true;
  }
  if (!shouldAskFirst(state)) return true;
  return askForAiConsent(state!.wording);
}

setAiConsentHandler((body) => askForAiConsent(wordingFromRefusal(body)));
